import React, { useRef, useEffect } from 'react';
import { useDispatch, connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { AiAssistChatActions } from './AiAssistChatActions';

import { IBusinessLogic } from 'businessLogic/initializer';
import { SkeletonLoader } from 'components/Shared/SkeletonLoader/SkeletonLoader';
import { useAiAssistChatMessages } from 'hooks/useAiAssistChatMessages';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { formatTextWithNewlines } from 'shared/utils';
import { setUserPrompt, sendChatMessage } from 'store/chat/slice';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { RootState } from 'store/store';
import { useSelector } from 'store/utils';
import { colors, fontSize } from 'styles/cp';

const HTML_PATTERN = /<[^>]*>/;
const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/AiAssist/Chat/AiAssistChat';

interface AiAssistChatProps {
  sale: any;
  payment: any;
  portal: string;
  env: string;
  wallet: any;
  realmId: string;
  ssrtid: string;
  authToken: string;
  insight: any;
}

export const _AiAssistChat: React.FC<AiAssistChatProps> = (props) => {
  const { sale, payment, portal, wallet, realmId, ssrtid, authToken, insight, env } = props;
  const dispatch = useDispatch<ThunkDispatch<RootState, IBusinessLogic, any>>();
  const { submitChatMessages } = useAiAssistChatMessages();

  const { messages, userPrompt, isLoading } = useSelector((state: RootState) => state.chat);
  const cdn = useSelector((state: RootState) => state.config.endpoints.cdn);
  const companyInfo = useSelector((state: RootState) => state.companyInfo);
  const companyName = companyInfoSelectors.nameSelector(companyInfo);
  const merchantEmail = companyInfoSelectors.emailSelector(companyInfo);
  const customerEmail = useSelector((state: RootState) => state.sale?.contact?.toEmails?.[0] || '');

  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [userPrompt]);

  useEffect(() => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'report',
      action: 'render',
      activityInfo: {
        experimentKey: 'AiAssistChat',
        feature: 'aiAssist',
        action: 'userParticipation',
      },
    });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 1000 && !HTML_PATTERN.test(value)) {
      dispatch(setUserPrompt(value));
    }
  };

  const handleSend = () => {
    if (!userPrompt.trim() || isLoading) return;

    dispatch(sendChatMessage());
    dispatch(setUserPrompt(''));
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only execute if messages array is not empty
      if (messages?.length === 0) {
        return;
      }
      // Log chat close when user leaves the page
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'report',
        action: 'render',
        activityInfo: {
          feature: 'aiAssist',
          action: 'chatClosed',
          messageCount: messages?.length,
          messages,
          closeMethod: 'pageUnload',
        },
      });
      submitChatMessages({
        messages,
        sale,
        payment,
        portal,
        env,
        wallet,
        realmId,
        ssrtid,
        authToken,
        insight,
        activityObject: 'AiAssistChat',
        logger,
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [messages]);

  return (
    <div className="cp-chat-root">
      <div className="cp-chat-header">
        <span className="cp-chat-header-title">Chat</span>
      </div>
      <div className="cp-chat-body">
        <>
          <div className="cp-chat-greeting">
            <div className="cp-chat-greeting-subtext">Hello</div>
            <div className="cp-chat-greeting-headline">How can I help?</div>
            <div className="cp-chat-greeting-subtext">Ask a question to start.</div>
          </div>
          {messages?.map((msg, idx) => (
            <div
              key={idx}
              className={`cp-chat-message-row ${
                msg.sender === 'user' ? 'cp-chat-message-row-user' : 'cp-chat-message-row-system'
              }`}
            >
              <div className="system-message-wrapper">
                {msg.sender === 'system' && (
                  <div className="cp-chat-system-text">
                    {formatTextWithNewlines(msg?.text)?.map((item, index) =>
                      item === null ? <br key={`br-${index}`} /> : item
                    )}
                  </div>
                )}
                <AiAssistChatActions
                  actionList={msg.actionList}
                  companyName={companyName}
                  merchantEmail={merchantEmail || ''}
                  customerEmail={typeof customerEmail === 'string' ? customerEmail : undefined}
                />
              </div>
              {msg.sender === 'user' && <div className="cp-chat-bubble-user">{msg.text}</div>}
            </div>
          ))}
          {isLoading && <SkeletonLoader />}
          <div ref={chatEndRef} />
        </>
      </div>
      <div className="cp-chat-input-row">
        <textarea
          ref={textareaRef}
          className="cp-chat-textarea"
          placeholder="Type or ask something"
          value={userPrompt}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={false}
          maxLength={1000}
        />
        {userPrompt.trim() && !isLoading && (
          <div className="cp-chat-send-button" onClick={handleSend} data-testid="send-button">
            <img src={`${cdn}/send-icon-arrow-green.svg`} alt="Send" />
          </div>
        )}
      </div>
      <div className="disclaimer">
        AI beta-results may be inaccurate and not reflect Intuit&apos;s views.
      </div>
      <style jsx>{`
        .cp-chat-root {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
          background: ${colors.white};
        }
        .cp-chat-header {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 15px 0;
          min-height: 56px;
        }
        .cp-chat-header-title {
          font-size: ${fontSize.sm};
          font-family: AvenirNextforINTUIT-Medium;
          color: ${colors.gray};
        }
        .cp-chat-body {
          flex: 1;
          overflow-y: auto;
          padding: 0 16px;
          display: flex;
          flex-direction: column;
          padding-top: 20px;
        }
        .cp-chat-greeting {
          margin-bottom: 32px;
          display: flex;
          flex-direction: column;
          align-self: flex-start;
          margin-top: auto;
        }
        .cp-chat-greeting-headline {
          font-size: 34px;
          font-family: AvenirNextforINTUIT-Bold;
          color: ${colors.gray};
        }
        .cp-chat-greeting-subtext {
          font-size: ${fontSize.sm};
          color: ${colors.gray};
        }
        .cp-chat-message-row {
          display: flex;
          margin-bottom: 12px;
        }
        .cp-chat-message-row-user {
          justify-content: flex-end;
        }
        .cp-chat-message-row-system {
          justify-content: flex-start;
        }
        .cp-chat-system-text {
          font-size: ${fontSize.xs};
          color: ${colors.gray};
          line-height: 1.5;
          word-break: break-word;
        }
        .cp-chat-bubble-user {
          background: ${colors.green};
          color: ${colors.white};
          border-radius: 12px;
          padding: 12px;
          font-size: ${fontSize.xs};
          max-width: 80%;
          text-align: right;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          word-break: break-word;
        }
        .cp-chat-input-row {
          border-top: 1.5px solid #eceef1;
          display: flex;
          align-items: flex-end;
          background: ${colors.white};
          padding: 0 16px;
          gap: 12px;
        }
        .cp-chat-textarea {
          width: 100%;
          border: none;
          border-radius: 8px;
          font-size: ${fontSize.sm};
          color: ${colors.gray};
          outline: none;
          padding: 18px 0;
          min-height: 24px;
          max-height: 200px;
          resize: none;
          box-sizing: border-box;
          line-height: 1.5;
          font-family: inherit;
          overflow-y: auto;
          transition: box-shadow 0.2s;
        }
        .cp-chat-textarea::placeholder {
          color: ${colors.gray03};
          opacity: 1;
        }
        .cp-chat-send-button {
          cursor: pointer;
          display: flex;
          transition: transform 0.2s;
          margin-bottom: 12px;
        }
        .cp-chat-send-button:hover {
          transform: scale(1.05);
        }
        .disclaimer {
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${colors.gray07};
          height: 32px;
          color: ${colors.gray02};
          font-size: 11px;
        }
        .system-message-wrapper {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
      `}</style>
    </div>
  );
};

function mapStateToProps(state: RootState) {
  const {
    config: { ssrtid, portal, env },
    insight,
    auth: { authToken, realmId },
    sale,
    wallet,
    payment,
  } = state;
  return {
    sale,
    insight,
    authToken,
    realmId,
    ssrtid,
    portal,
    env,
    wallet,
    payment,
  };
}

export const AiAssistChat = connect(mapStateToProps)(_AiAssistChat);
